// Copyright (c) 2026 Endel Dreyer.
//
// This software is released under the MIT License.
// https://opensource.org/license/MIT
//
// colyseus.js@0.18.2
import { now } from './core/utils.mjs';

/**
 * Stub clock returned by {@link Room.clock} until the JOIN_ROOM handshake
 * reveals whether the room declared input.
 *
 * - `serverNow()` falls back to the client's own `performance.now()` — a
 *   monotonic, non-offset-corrected timestamp. Good enough for any consumer
 *   that just wants "a monotonic ms reading."
 * - `rtt()` / `smoothedRtt()` return `0` (no samples available).
 * - `sample()` is a no-op.
 *
 * Shared, frozen singleton — costs nothing to keep around for rooms that
 * never call `defineInput()`. The Room replaces it with a real
 * {@link RoomClockImpl} during handshake when input is declared.
 */
const NULL_CLOCK = Object.freeze({
    now: () => now(),
    serverNow: () => now(),
    renderNow: () => now(),
    rtt: () => 0,
    smoothedRtt: () => 0,
    jitter: () => 0,
    lastServerTime: () => 0,
    patchInterval: () => 0,
    setPatchInterval: (_ms) => { },
    sample: (_sNow, _rttSample) => { },
});
/**
 * Per-room clock-sync + RTT estimator, fed by the {@link ProtocolModifier.TIMED}
 * prefix the server prepends to state messages when the room declared input
 * via `defineInput()`.
 *
 * Two independent estimates are tracked:
 *
 * - **Clock offset** (`serverNow()`): the delta between server `performance.now()`
 *   and the client's. Seeded by the first sample (offset-only or RTT-valid)
 *   then EMA-smoothed. Used so client-side comparisons against
 *   server-stamped deadlines (`invulnUntil`, `hitTime`, etc.) line up.
 *
 * - **Round-trip time** (`rtt()` / `smoothedRtt()`): computed by correlating
 *   the server-echoed `lastInputSeq` with the client's own send-time table.
 *   Seeded *only* by the first RTT-valid sample (separate from the offset
 *   seed) — otherwise the first valid sample would EMA-blend from 0 and
 *   strand the smoothed value at ~10% of reality, after which the outlier
 *   guard would reject every subsequent real sample.
 *
 * Doesn't know about transports, schemas, input, or Room internals — the Room
 * calls {@link sample} when a TIMED prefix arrives, passing a pre-computed RTT
 * sample (the input round-trip is tracked by the InputHandle). Pure math.
 */
class RoomClockImpl {
    /** Default exponential-smoothing weight for offset + RTT EMA. */
    static EMA_ALPHA = 0.1;
    /** Default slew time-constant (ms) for {@link renderNow}. ~250 ms: offset
     *  corrections smear over a few frames instead of popping, while the render
     *  timeline still tracks `serverNow()` closely in steady state. */
    static RENDER_TAU = 250;
    /** Gap (ms) past which {@link renderNow} SNAPS to `serverNow()` instead of
     *  slewing. Slewing a large gap (join warmup while the offset EMA is still
     *  converging, a route-change offset jump, a tab-resume stall) would render
     *  a visible standing lag; only wobble-scale gaps get smoothed. */
    static RENDER_SNAP = 250;
    /**
     * RTT samples greater than `outlierFactor × smoothedRtt` are rejected.
     * Catches tab-resume spikes once the smoothed value has converged; the
     * separate `_rttHasSample` seed prevents this from clamping early
     * legitimate samples to a stranded baseline.
     */
    static RTT_OUTLIER_X = 4;
    /** RFC 3550 jitter EMA gain (1/16) — slower than the RTT/offset EMA so the
     *  reported jitter is a steady readout rather than a per-patch flicker. */
    static JITTER_GAIN = 1 / 16;
    /** Arrival gaps beyond `JITTER_STALL_X ×` the cadence (a tab-resume stall), and
     *  sub-cadence bursts (mult 0), are skipped so they don't spike the jitter EMA. */
    static JITTER_STALL_X = 4;
    /**
     * Clock-offset jitter gate. An offset sample is only folded into the EMA when
     * its RTT is within `RTT_GATE_FACTOR ×` the windowed-minimum RTT — i.e. the
     * packet traversed near-empty queues, so its `rtt/2` one-way estimate (and
     * thus the offset) is least corrupted by jitter. Higher-RTT samples carry
     * proportionally more jitter and are dropped (the offset just holds). This is
     * the NTP/QUIC pattern: EMA-smooth the value you report, but filter the input
     * by the low-delay floor — purely a VARIANCE reduction on `serverNow()`, which
     * is what steadies the stamped reckonTime. The held offset's small bias is
     * harmless (it cancels: predict + rewind share the stamped instant).
     */
    static RTT_GATE_FACTOR = 1.2;
    /** Sliding window (ms) over which the RTT floor (gate reference) is tracked.
     *  Long enough to hold a good low-jitter sample; on a route change the stale
     *  floor expires within this horizon and the gate re-opens. */
    static RTT_GATE_WINDOW = 10_000;
    /** Post-reset warmup: the first `RTT_GATE_WARMUP` RTT-valid samples BYPASS the
     *  gate (pure EMA), so the offset converges at baseline speed after a reset /
     *  reconnect. The gate drops samples, which otherwise stretches convergence —
     *  worst at high RTT, where it showed as an inflated offset.std until settled.
     *  ~3× the EMA time-constant (1/α = 10) ⇒ converged before the gate engages for
     *  steady-state variance reduction. `0` disables the warmup (gate from sample 1). */
    static RTT_GATE_WARMUP = 30;
    _clockOffset = 0; // serverTime - clientTime at sample time
    _clockHasSample = false;
    _offsetCount = 0; // RTT-valid offset samples since reset (gate warmup)
    // Sliding-window-minimum of RTT (monotonic deque: values increasing front→back,
    // front = windowed min). Parallel number arrays → no per-sample object alloc.
    _rttFloorT = []; // sample arrival times (tNow), aligned with _rttFloorV
    _rttFloorV = []; // RTT values, monotonically increasing
    _rtt = 0; // most recent RTT sample (ms)
    _smoothedRtt = 0; // EMA over RTT samples
    _rttHasSample = false;
    _jitter = 0; // EMA of patch-arrival deviation from the cadence (ms)
    _lastRecvTime = -1; // arrival time of the previous patch; -1 until the first
    _lastServerTime = 0; // raw sNow of the last patch (snapshot stamp)
    _patchInterval = 0; // server patchRate (ms); 0 until advertised
    _renderTau = RoomClockImpl.RENDER_TAU; // slew time-constant (ms); 0 disables
    _renderSn = 0; // slew-limited render-clock reading (ms since room start)
    _renderSnAt = 0; // local time (now()) the render clock last advanced
    /** Estimated server clock: **milliseconds since room start** (the server's
     *  `clock.elapsedTime`, reconstructed via the wire `sNow` + local offset).
     *  NOT raw `performance.now()` — a portable integer-ms timeline the server's
     *  own time-keyed logic shares, so client-side reckon stays in phase.
     *  Returns the local clock until the first sample lands. */
    serverNow() {
        return now() + this._clockOffset;
    }
    /** Slew-limited render timeline — see {@link RoomClockLike.renderNow}.
     *  Free-runs at 1 ms/ms and servos toward {@link serverNow} with the
     *  time-constant set by {@link setRenderTau} (default {@link RENDER_TAU});
     *  `τ ≤ 0` disables the slew and returns `serverNow()` verbatim. Idempotent
     *  within a frame: it advances only on the first call each frame (guarded on
     *  the local clock), so reading it once per tracked entity doesn't over-step
     *  it. */
    renderNow() {
        const target = this.serverNow();
        if (this._renderTau <= 0) {
            return target;
        }
        const t = now();
        // Seed on first use — jump straight to the current server-present.
        if (this._renderSn === 0) {
            this._renderSn = target;
            this._renderSnAt = t;
            return this._renderSn;
        }
        const dt = Math.min(t - this._renderSnAt, 100); // clamp tab-resume stalls
        if (dt < 0.5) {
            return this._renderSn;
        } // same frame — advance once
        this._renderSnAt = t;
        this._renderSn += dt; // free-run at 1 ms/ms
        // Snap past large gaps rather than slew them (see RENDER_SNAP) — a
        // standing lag reads worse than one clean jump.
        if (Math.abs(target - this._renderSn) > RoomClockImpl.RENDER_SNAP) {
            this._renderSn = target;
            return this._renderSn;
        }
        this._renderSn += (target - this._renderSn) * (1 - Math.exp(-dt / this._renderTau)); // servo toward server-present
        return this._renderSn;
    }
    /** Set the {@link renderNow} slew time-constant (ms). `≤ 0` disables slewing
     *  (renderNow == serverNow). Larger = smoother, but offset corrections lag
     *  longer. */
    setRenderTau(milliseconds) {
        this._renderTau = milliseconds > 0 ? milliseconds : 0;
    }
    /** Local monotonic clock (ms): the client's own reading WITHOUT the server
     *  offset — the base {@link serverNow} adds the offset to. Use it for
     *  self-imposed relative cooldowns (`now() - lastAction >= COOLDOWN_MS`): they
     *  need only a steady rate, not clock sync, so this avoids the offset-EMA
     *  jitter `serverNow()` carries. @see RoomClockLike.now */
    now() {
        return now();
    }
    /** Most recent RTT sample (ms). `0` until the first RTT-valid sample lands. */
    rtt() {
        return this._rtt;
    }
    /** EMA-smoothed RTT (ms). `0` until the first RTT-valid sample lands. Prefer this for forward-prediction. */
    smoothedRtt() {
        return this._smoothedRtt;
    }
    /** Connection jitter (ms): RFC 3550-style interarrival jitter — see
     *  {@link RoomClockLike.jitter}. `0` until the cadence is known and two patches land. */
    jitter() {
        return this._jitter;
    }
    /** Server-encode time (raw `sNow`) of the most recent patch. Pair with
     *  {@link serverNow} for the snapshot age (`serverNow() − lastServerTime()`).
     *  `0` until the first sample. */
    lastServerTime() {
        return this._lastServerTime;
    }
    /** Server snapshot cadence (`patchRate`, ms); `0` until the handshake
     *  advertises it. @see RoomClockLike.patchInterval */
    patchInterval() {
        return this._patchInterval;
    }
    /** Set the server snapshot cadence (ms), from the input handshake's
     *  advertised `patchRate`. Non-positive values clear it back to `0`. */
    setPatchInterval(milliseconds) {
        this._patchInterval = milliseconds > 0 ? milliseconds : 0;
    }
    /**
     * Feed a decoded TIMED sample. The input round-trip lives on the
     * {@link InputHandle} now — the Room hands us a pre-computed RTT sample.
     *
     * @param sNow       Server clock (ms since room start, `clock.elapsedTime`)
     *                   → clock offset.
     * @param rttSample  Round-trip time (ms) for the input ack this packet
     *                   carried, or `< 0` if none (no matching send / no input
     *                   yet). Filtered + EMA-smoothed here.
     */
    sample(sNow, rttSample) {
        const tNow = now();
        const a = RoomClockImpl.EMA_ALPHA;
        // Connection jitter (RFC 3550 interarrival): how far this patch's arrival
        // interval strays from the advertised cadence — measured against the cadence,
        // NOT `sNow` (only refreshed at the sim-tick rate, so a sim/patch rate mismatch
        // would beat in as phantom jitter). Idle/dropped patches round to a whole
        // multiple; bursts (mult 0) and stalls (> X) are skipped.
        const PI = this._patchInterval;
        if (this._lastRecvTime >= 0 && PI > 0) {
            const gap = tNow - this._lastRecvTime;
            const mult = Math.round(gap / PI);
            if (mult >= 1 && mult <= RoomClockImpl.JITTER_STALL_X) {
                this._jitter += (Math.abs(gap - mult * PI) - this._jitter) * RoomClockImpl.JITTER_GAIN;
            }
        }
        this._lastRecvTime = tNow;
        // Stamp the snapshot's server-encode time (raw, un-reconstructed).
        this._lastServerTime = sNow;
        // Reject impossible / outlier RTT (tab-resume spikes once converged).
        if (rttSample < 0) {
            rttSample = -1;
        }
        else if (this._smoothedRtt > 0 && rttSample > this._smoothedRtt * RoomClockImpl.RTT_OUTLIER_X) {
            rttSample = -1;
        }
        // Clock offset: refreshed every patch (sNow advances each patch). Prefer
        // the RTT-corrected estimate when a fresh sample exists, else OWL-biased.
        const offsetSample = rttSample >= 0 ? sNow + rttSample / 2 - tNow : sNow - tNow;
        if (!this._clockHasSample) {
            this._clockOffset = offsetSample;
            this._clockHasSample = true;
            if (rttSample >= 0) {
                this.pushRttFloor(rttSample, tNow);
                this._offsetCount = 1;
            }
        }
        else if (rttSample >= 0) {
            // Track the windowed-min RTT and gate: only low-jitter samples update the
            // offset (see RTT_GATE_FACTOR) — EXCEPT during the post-reset warmup, when
            // the gate is bypassed so convergence isn't stretched (see RTT_GATE_WARMUP).
            // rttSample<0 (no ack this packet) skips the offset entirely — an
            // uncorrected `sNow-tNow` is the noisiest kind, so let the offset hold.
            const floor = this.pushRttFloor(rttSample, tNow);
            const warming = this._offsetCount < RoomClockImpl.RTT_GATE_WARMUP;
            this._offsetCount++;
            if (warming || rttSample <= floor * RoomClockImpl.RTT_GATE_FACTOR) {
                this._clockOffset = this._clockOffset * (1 - a) + offsetSample * a;
            }
        }
        // RTT: seeded on the first valid sample (separate flag — see class doc).
        if (rttSample >= 0) {
            this._rtt = rttSample;
            if (!this._rttHasSample) {
                this._smoothedRtt = rttSample;
                this._rttHasSample = true;
            }
            else {
                this._smoothedRtt = this._smoothedRtt * (1 - a) + rttSample * a;
            }
        }
    }
    /**
     * Push an RTT sample into the sliding-window-minimum deque and return the
     * current windowed-min RTT (the jitter-free floor the offset gate references).
     * Standard monotonic-deque algorithm — O(1) amortized, the deque holds only
     * descending "record-low" candidates (typically 1–few entries).
     */
    pushRttFloor(rttSample, tNow) {
        const T = this._rttFloorT, V = this._rttFloorV;
        // Drop back entries no lower than this sample — they can never be the min
        // again while this newer, ≤ sample is in the window.
        while (V.length > 0 && V[V.length - 1] >= rttSample) {
            V.pop();
            T.pop();
        }
        V.push(rttSample);
        T.push(tNow);
        // Expire entries older than the window from the front.
        const cutoff = tNow - RoomClockImpl.RTT_GATE_WINDOW;
        while (T.length > 0 && T[0] < cutoff) {
            T.shift();
            V.shift();
        }
        return V[0]; // front = windowed minimum
    }
    /** Reset all state. Useful on reconnect when the room rebuilds context. */
    reset() {
        this._clockOffset = 0;
        this._clockHasSample = false;
        this._offsetCount = 0;
        this._rtt = 0;
        this._smoothedRtt = 0;
        this._rttHasSample = false;
        this._jitter = 0;
        this._lastRecvTime = -1;
        this._lastServerTime = 0;
        this._rttFloorT.length = 0;
        this._rttFloorV.length = 0;
        this._renderSn = 0; // τ is config, not state — it survives reset
        this._renderSnAt = 0;
    }
}

export { NULL_CLOCK, RoomClockImpl };
//# sourceMappingURL=RoomClock.mjs.map
